import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from "recharts";
import {
  Box,
  Button,
  Typography,
  Modal,
  Slider,
  CircularProgress,
  useTheme,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Fab
} from "@mui/material";
import {
  PlayArrow,
  Pause,
  Stop,
  Upload,
  GridOn,
  GridOff
} from "@mui/icons-material";
import { format, parseISO } from 'date-fns';
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { usePortData } from "../../contexts/PortDataContext";

const CHART_DISPLAY_NAMES = {
  thrust: "Thrust",
  ambientTemp: "Ambient Temp (°C)",
  objectTemp: "Object Temp (°C)",
  weight: "Weight (kg)",
  current: "Current (A)",
  rpm: "RPM",
  dutyCycle: "Duty Cycle (%)",
};

const CHART_COLORS = {
  thrust: '#AF7AA1',
  ambientTemp: '#4E79A7',
  objectTemp: '#F28E2B',
  weight: '#E15759',
  current: '#76B7B2',
  rpm: '#59A14F',
  dutyCycle: '#EDC948'
};

const MAX_VISIBLE_POINTS = 100;
const PLAYBACK_INTERVAL_MS = 100;
const EXPORT_METRIC_KEYS = Object.keys(CHART_DISPLAY_NAMES);

const getMetricStatistics = (data, metricKey) => {
  const values = data
    .map((point) => Number(point[metricKey]))
    .filter(Number.isFinite);

  if (!values.length) {
    return { samples: 0, minimum: "", maximum: "", average: "" };
  }

  const total = values.reduce((sum, value) => sum + value, 0);
  return {
    samples: values.length,
    minimum: Math.min(...values),
    maximum: Math.max(...values),
    average: total / values.length,
  };
};

const createExportFileName = (datasetName, extension) => {
  const safeName = (datasetName || "thrust-stand-data")
    .replace(/[^a-z0-9-_]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
  const timestamp = format(new Date(), "yyyyMMdd-HHmmss");
  return `${safeName || "thrust-stand-data"}-${timestamp}.${extension}`;
};

const HistoryVisualization = () => {
  const theme = useTheme();
  const {
    selectedPort,
    setSelectedPort,
    availablePorts,
    serialStatus,
    isRunning,
    liveData,
    connectionMessage,
    refreshPorts,
    connectSerial,
    disconnectSerial,
    startThrust,
    stopThrust,
    sendThrottle,
  } = usePortData();
  const [throttle, setThrottle] = useState(0);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed] = useState(1);
  const [currentTimeIndex, setCurrentTimeIndex] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [fileData, setFileData] = useState(null);
  const [selectedCharts, setSelectedCharts] = useState({
    thrust: true,
    ambientTemp: true,
    objectTemp: true,
    weight: true,
    current: true,
    rpm: true,
    dutyCycle: true,
  });
  const fileInputRef = useRef(null);
  const [showGrid, setShowGrid] = useState(true);
  const [graphsPerRow, setGraphsPerRow] = useState(2);
  const [showFloatingButton, setShowFloatingButton] = useState(true);
  const controlsRef = useRef(null);
  const liveDataInitializedRef = useRef(false);

  useEffect(() => {
    if (!liveData.length) return;

    if (!liveDataInitializedRef.current) {
      liveDataInitializedRef.current = true;
      setSelectedCharts({
        thrust: true,
        ambientTemp: true,
        objectTemp: true,
        weight: true,
        current: true,
        rpm: true,
        dutyCycle: true,
      });
    }

    setSelectedDataset((current) => {
      if (current?.id?.startsWith("uploaded_")) return current;
      return {
        id: "live",
        name: "Live thrust-stand data",
        timestamp: liveData[0].time,
        data: liveData,
      };
    });
    setCurrentTimeIndex(liveData.length - 1);
  }, [liveData]);

  // Calculate visible data points
  const visibleData = useMemo(() => {
    if (!selectedDataset || !selectedDataset.data.length) return [];

    const startIdx = Math.max(0, currentTimeIndex - MAX_VISIBLE_POINTS);
    const endIdx = currentTimeIndex;
    
    return selectedDataset.data.slice(startIdx, endIdx + 1);
  }, [selectedDataset, currentTimeIndex]);

  // Current data point
  const currentDataPoint = useMemo(() => {
    if (!selectedDataset || currentTimeIndex < 0 || currentTimeIndex >= selectedDataset.data.length) {
      return null;
    }
    return selectedDataset.data[currentTimeIndex];
  }, [selectedDataset, currentTimeIndex]);

  // Dataset metrics

  // Animation effect with speed control
  useEffect(() => {
    if (!isPlaying || !selectedDataset) return;

    let animationFrameId;
    let lastTimestamp = performance.now();
    let accumulatedTime = 0;

    const speedMultiplier = playbackSpeed < 1 ? 
      playbackSpeed * playbackSpeed :
      playbackSpeed;

    const updatePlayback = (timestamp) => {
      const deltaTime = timestamp - lastTimestamp;
      lastTimestamp = timestamp;
    
      if (deltaTime > 0) {
        const timePerPoint = PLAYBACK_INTERVAL_MS / speedMultiplier;
        accumulatedTime += deltaTime;

        const pointsToAdvance = Math.floor(accumulatedTime / timePerPoint);

        if (pointsToAdvance > 0) {
          setCurrentTimeIndex(prev => {
            const newIndex = prev + pointsToAdvance;
            if (newIndex >= selectedDataset.data.length - 1) {
              setIsPlaying(false);
              return selectedDataset.data.length - 1;
            }
            return newIndex;
          });
          accumulatedTime -= pointsToAdvance * timePerPoint;
        }
      }
  
      if (isPlaying) {
        animationFrameId = requestAnimationFrame(updatePlayback);
      }
    };

    animationFrameId = requestAnimationFrame(updatePlayback);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [isPlaying, playbackSpeed, selectedDataset]);

  // Handle play/pause
  const togglePlayback = useCallback(() => {
    if (!selectedDataset) return;

    if (isPlaying) {
      setIsPlaying(false);
    } else {
      // If at end, restart from beginning
      if (currentTimeIndex >= selectedDataset.data.length - 1) {
        setCurrentTimeIndex(0);
      }
      setIsPlaying(true);
    }
  }, [isPlaying, selectedDataset, currentTimeIndex]);

  // Handle stop

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadProgress(0);
    setShowUploadModal(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        setTimeout(() => {
          const arrayBuffer = e.target.result;
          const data = new Uint8Array(arrayBuffer);

          const processedData = [];
          const startTime = new Date();
          
          for (let i = 0; i < data.length; i += 24) {
            processedData.push({
              time: new Date(startTime.getTime() + i * 1000).toISOString(),
              ambientTemp: data[i] / 2,
              objectTemp: data[i+1] / 2,
              weight: data[i+2] * 0.1,
              current: data[i+3] * 0.05,
              rpm: data[i+4] * 10,
              dutyCycle: data[i+5]
            });
          }

          setFileData({
            id: `uploaded_${Date.now()}`,
            name: file.name.replace('.bin', ''),
            timestamp: new Date().toISOString(),
            data: processedData
          });

          setUploadProgress(100);
        }, 1000);
      } catch (error) {
        console.error("Error processing file:", error);
        setUploadProgress(0);
      }
    };

    reader.onprogress = (e) => {
      if (e.lengthComputable) {
        setUploadProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    reader.readAsArrayBuffer(file);
  };

  // Use the uploaded data
  const handleUseUploadedData = useCallback(() => {
    if (!fileData) return;
    
    setSelectedDataset(fileData);
    setCurrentTimeIndex(0);
    setShowUploadModal(false);
    setFileData(null);
    setUploadProgress(0);
    setSelectedCharts({
      thrust: false,
      ambientTemp: true,
      objectTemp: true,
      weight: true,
      current: true,
      rpm: true,
      dutyCycle: true,
    });
  }, [fileData]);

  const handleUseLiveData = useCallback(() => {
    if (!liveData.length) return;
    setSelectedDataset({
      id: "live",
      name: "Live thrust-stand data",
      timestamp: liveData[0].time,
      data: liveData,
    });
    setCurrentTimeIndex(liveData.length - 1);
    setSelectedCharts({
      thrust: true,
      ambientTemp: true,
      objectTemp: true,
      weight: true,
      current: true,
      rpm: true,
      dutyCycle: true,
    });
  }, [liveData]);

  const handleThrottleChange = useCallback((event, value) => {
    const nextValue = Number(value);
    setThrottle(nextValue);
    sendThrottle(nextValue);
  }, [sendThrottle]);

  const handleStartThrottle = useCallback(() => {
    setThrottle(0);
    startThrust();
  }, [startThrust]);

  const handleStopThrottle = useCallback(() => {
    setThrottle(0);
    sendThrottle(0);
    stopThrust();
  }, [sendThrottle, stopThrust]);

  const handleExportPdf = useCallback(() => {
    if (!selectedDataset?.data?.length) return;

    const report = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const data = selectedDataset.data;
    const generatedAt = new Date();
    const summaryRows = EXPORT_METRIC_KEYS.map((metricKey) => {
      const statistics = getMetricStatistics(data, metricKey);
      return [
        CHART_DISPLAY_NAMES[metricKey],
        statistics.samples,
        statistics.minimum === "" ? "-" : statistics.minimum.toFixed(3),
        statistics.maximum === "" ? "-" : statistics.maximum.toFixed(3),
        statistics.average === "" ? "-" : statistics.average.toFixed(3),
      ];
    });

    report.setFontSize(18);
    report.text("Thrust Stand Data Report", 14, 16);
    report.setFontSize(10);
    report.text(`Dataset: ${selectedDataset.name || selectedDataset.id || "Dataset"}`, 14, 23);
    report.text(`Generated: ${format(generatedAt, "yyyy-MM-dd HH:mm:ss")}`, 14, 29);
    report.text(`Data points: ${data.length}`, 14, 35);

    report.autoTable({
      startY: 41,
      head: [["Metric", "Samples", "Minimum", "Maximum", "Average"]],
      body: summaryRows,
      theme: "grid",
      styles: { fontSize: 8 },
      headStyles: { fillColor: [45, 55, 72] },
    });

    const readingsStartY = (report.lastAutoTable?.finalY || 41) + 8;
    report.setFontSize(12);
    report.text("Timestamped Readings", 14, readingsStartY);
    report.autoTable({
      startY: readingsStartY + 4,
      head: [["Timestamp", ...EXPORT_METRIC_KEYS.map((key) => CHART_DISPLAY_NAMES[key])]],
      body: data.map((point) => [
        point.time ? format(new Date(point.time), "yyyy-MM-dd HH:mm:ss.SSS") : "",
        ...EXPORT_METRIC_KEYS.map((key) => {
          const value = Number(point[key]);
          return Number.isFinite(value) ? value.toFixed(3) : "";
        }),
      ]),
      theme: "striped",
      styles: { fontSize: 6, cellPadding: 1.2 },
      headStyles: { fillColor: [45, 55, 72], fontSize: 6 },
      margin: { left: 8, right: 8 },
      horizontalPageBreak: true,
    });

    report.save(createExportFileName(selectedDataset.name, "pdf"));
  }, [selectedDataset]);

  const handleExportExcel = useCallback(() => {
    if (!selectedDataset?.data?.length) return;

    const data = selectedDataset.data;
    const summary = EXPORT_METRIC_KEYS.map((metricKey) => {
      const statistics = getMetricStatistics(data, metricKey);
      return {
        Metric: CHART_DISPLAY_NAMES[metricKey],
        Samples: statistics.samples,
        Minimum: statistics.minimum,
        Maximum: statistics.maximum,
        Average: statistics.average,
      };
    });
    const readings = data.map((point) => ({
      Timestamp: point.time ? new Date(point.time).toISOString() : "",
      ...Object.fromEntries(
        EXPORT_METRIC_KEYS.map((key) => [
          CHART_DISPLAY_NAMES[key],
          Number.isFinite(Number(point[key])) ? Number(point[key]) : "",
        ])
      ),
    }));

    const workbook = XLSX.utils.book_new();
    const summarySheet = XLSX.utils.json_to_sheet(summary);
    const readingsSheet = XLSX.utils.json_to_sheet(readings);
    summarySheet["!cols"] = [{ wch: 28 }, { wch: 10 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
    readingsSheet["!cols"] = [
      { wch: 26 },
      ...EXPORT_METRIC_KEYS.map(() => ({ wch: 22 })),
    ];
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");
    XLSX.utils.book_append_sheet(workbook, readingsSheet, "Readings");
    XLSX.writeFile(workbook, createExportFileName(selectedDataset.name, "xlsx"));
  }, [selectedDataset]);

  // Format time for display
  const formatTime = (time) => {
    return format(parseISO(time), 'HH:mm:ss.SSS');
  };

  // Check if controls are visible
  useEffect(() => {
    const handleScroll = () => {
      if (controlsRef.current) {
        const rect = controlsRef.current.getBoundingClientRect();
        setShowFloatingButton(rect.bottom < 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Render chart for a specific metric
  const renderMetricChart = (metricKey) => (
    <Box
      key={metricKey}
      sx={{
        height: '300px',
        width: '100%',
        p: 2,
        backgroundColor: theme.palette.tertiary.main,
        borderRadius: 2,
        boxShadow: theme.shadows[1],
        border: `1px solid ${theme.palette.border.color}`
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: CHART_COLORS[metricKey] }}>
          {CHART_DISPLAY_NAMES[metricKey]}
        </Typography>
        {currentDataPoint && (
          <Chip 
            label={`Current: ${Number(currentDataPoint[metricKey] ?? 0).toFixed(2)}`}
            size="small"
            sx={{ 
              backgroundColor: theme.palette.mode === 'dark' ? 
                `${CHART_COLORS[metricKey]}30` : 
                `${CHART_COLORS[metricKey]}10`,
              borderColor: CHART_COLORS[metricKey],
              color: theme.palette.text.color
            }}
          />
        )}
      </Box>
      <ResponsiveContainer width="100%" height="85%">
        <LineChart
          data={visibleData}
          margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
        >
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.border.color} />}
          <XAxis
            dataKey="time"
            tickFormatter={(time) => format(parseISO(time), 'HH:mm:ss')}
            tick={{ fontSize: 12, fill: theme.palette.text.color }}
            stroke={theme.palette.text.secondary}
          />
          <YAxis
            stroke={theme.palette.text.secondary}
            domain={['auto', 'auto']}
            tick={{ fill: theme.palette.text.color }}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload || !payload.length) return null;
              
              const data = payload[0].payload;
              return (
                <Box sx={{
                  background: theme.palette.tertiary.main,
                  border: `1px solid ${theme.palette.border.color}`,
                  borderRadius: 1,
                  p: 1.5,
                  boxShadow: theme.shadows[2]
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: theme.palette.text.color }}>
                    {formatTime(data.time)}
                  </Typography>
                  <Typography variant="body1" sx={{ color: CHART_COLORS[metricKey] }}>
                    {CHART_DISPLAY_NAMES[metricKey]}: <strong>{Number(data[metricKey] ?? 0).toFixed(2)}</strong>
                  </Typography>
                </Box>
              );
            }}
          />
          <Line
            type="monotone"
            dataKey={metricKey}
            stroke={CHART_COLORS[metricKey]}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 6, strokeWidth: 2 }}
            isAnimationActive={true}
            animationDuration={300}
          />
          {currentDataPoint && (
            <ReferenceLine 
              x={currentDataPoint.time} 
              stroke={CHART_COLORS[metricKey]} 
              strokeDasharray="3 3" 
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );

  // Count of selected charts

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      minHeight: '100vh',
      p: 3,
      backgroundColor: theme.palette.primary.main
    }}>
      {/* Header */}
      <Box sx={{
        mb: 4,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 3,
        flexWrap: 'wrap'
      }}>
        <Box>
          <Typography variant="h4" sx={{
            fontWeight: 'bold',
            color: theme.palette.text.color,
            mb: 1
          }}>
            Data Visualization
          </Typography>
          <Typography variant="subtitle1" sx={{ color: theme.palette.text.secondary }}>
            Point-by-point animation with configurable display
          </Typography>
        </Box>

        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 1,
          minWidth: 320
        }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel id="visualization-com-port-label">COM device</InputLabel>
              <Select
                labelId="visualization-com-port-label"
                value={selectedPort}
                label="COM device"
                onChange={(event) => setSelectedPort(event.target.value)}
                disabled={serialStatus.connected || isRunning}
              >
                {availablePorts.length === 0 ? (
                  <MenuItem value="" disabled>No Arduino or COM device detected</MenuItem>
                ) : availablePorts.map((port) => (
                  <MenuItem key={port.device} value={port.device}>
                    {port.device} - {port.description || 'Serial device'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              color="success"
              startIcon={<PlayArrow />}
              disabled={serialStatus.connected || !selectedPort}
              onClick={() => connectSerial(selectedPort)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Connect Arduino
            </Button>

            <Button
              variant="contained"
              color="error"
              startIcon={<Stop />}
              disabled={!serialStatus.connected}
              onClick={disconnectSerial}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Disconnect Arduino
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={() => refreshPorts()}
              disabled={isRunning}
            >
              Refresh
            </Button>

            {serialStatus.connected && !isRunning && (
              <Button variant="contained" color="success" onClick={startThrust}>
                Start Live Data
              </Button>
            )}
          </Box>
          <Typography
            variant="caption"
            sx={{
              color: serialStatus.connected
                ? theme.palette.success.main
                : theme.palette.text.secondary
            }}
          >
            {serialStatus.connected
              ? `Arduino serial connection active on ${serialStatus.port}`
              : connectionMessage || 'Arduino is not connected'}
          </Typography>
        </Box>
      </Box>

      {/* Live Throttle and Reporting */}
      <Box
        sx={{
          mb: 3,
          p: 2.5,
          display: 'flex',
          gap: 4,
          alignItems: 'stretch',
          flexWrap: 'wrap',
          backgroundColor: theme.palette.secondary.main,
          borderRadius: 2,
          border: `1px solid ${theme.palette.border.color}`,
        }}
      >
        <Box sx={{ flex: '1 1 520px' }}>
          <Typography variant="h6" sx={{ color: theme.palette.text.color, mb: 0.5 }}>
            Live Throttle Control
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
            Start the live session, then adjust the throttle sent to the connected thrust stand.
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<PlayArrow />}
              onClick={handleStartThrottle}
              disabled={!serialStatus.connected || isRunning}
            >
              Start Throttle
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<Stop />}
              onClick={handleStopThrottle}
              disabled={!isRunning}
            >
              Stop Throttle
            </Button>
            <Chip
              label={isRunning ? 'Live session running' : 'Live session stopped'}
              color={isRunning ? 'success' : 'default'}
              variant={isRunning ? 'filled' : 'outlined'}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography sx={{ minWidth: 105, color: theme.palette.text.color }}>
              Throttle: {throttle}%
            </Typography>
            <Slider
              value={throttle}
              onChange={handleThrottleChange}
              min={0}
              max={100}
              step={1}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${value}%`}
              disabled={!isRunning}
              color="warning"
              aria-label="Throttle percentage"
            />
          </Box>
        </Box>

        <Box
          sx={{
            flex: '0 1 330px',
            pl: { xs: 0, md: 3 },
            borderLeft: { xs: 'none', md: `1px solid ${theme.palette.border.color}` },
          }}
        >
          <Typography variant="h6" sx={{ color: theme.palette.text.color, mb: 0.5 }}>
            Export / Report
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
            Export the selected live or uploaded dataset with metric summaries and complete readings.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={handleExportPdf}
              disabled={!selectedDataset?.data?.length}
            >
              Export PDF Report
            </Button>
            <Button
              variant="outlined"
              onClick={handleExportExcel}
              disabled={!selectedDataset?.data?.length}
            >
              Export Excel
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Controls Section */}
      <Box 
        ref={controlsRef}
        sx={{ 
          mb: 3,
          p: 2,
          backgroundColor: theme.palette.secondary.main,
          borderRadius: 2,
          boxShadow: theme.shadows[1],
          border: `1px solid ${theme.palette.border.color}`
        }}
      >
         <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          {/* <IconButton
            onClick={togglePlayback}
            disabled={!selectedDataset}
            color="primary"
            sx={{ 
              width: 48, 
              height: 48,
              backgroundColor: theme.palette.primary.dark,
              color: theme.palette.primary.contrastText,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark
              },
              '&:disabled': {
                backgroundColor: theme.palette.action.disabledBackground
              }
            }}
          >
            {isPlaying ? <Pause /> : <PlayArrow />}
          </IconButton>
          
          <IconButton
            onClick={handleStop}
            disabled={!selectedDataset}
            color="secondary"
            sx={{
              color: theme.palette.text.color,
              '&:disabled': {
                color: theme.palette.text.disabled
              }
            }}
          >
          //   <Stop /> 
          // </IconButton> 

          {/* <Slider
            value={currentTimeIndex}
            onChange={(e, newValue) => setCurrentTimeIndex(newValue)}
            min={0}
            max={selectedDataset ? selectedDataset.data.length - 1 : 0}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => {
              if (!selectedDataset) return '0';
              return `${value + 1}/${selectedDataset.data.length}`;
            }}
            sx={{ 
              flexGrow: 1, 
              mx: 2,
              
            }}
            color="warning"
            disabled={!selectedDataset}
          /> */}

          {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200 }}>
            <FastRewind fontSize="small" sx={{ color: theme.palette.text.color }} />
            <Slider
              value={playbackSpeed}
              onChange={(e, newValue) => setPlaybackSpeed(newValue)}
              min={MIN_PLAYBACK_SPEED}
              max={MAX_PLAYBACK_SPEED}
              step={0.1}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${value.toFixed(1)}x`}
              color="info"
            />
            <FastForward fontSize="small" sx={{ color: theme.palette.text.color }} />
          </Box> */}

          <Button
            variant="outlined"
            onClick={handleUseLiveData}
            disabled={!liveData.length}
            sx={{ ml: 'auto', whiteSpace: 'nowrap' }}
          >
            Live Data ({liveData.length})
          </Button>

          <Button
            variant="contained"
            startIcon={<Upload sx={{ color: theme.palette.primary.contrastText }} />}
            onClick={() => fileInputRef.current.click()}
            sx={{ 
              backgroundColor: theme.palette.primary.dark,
              color: theme.palette.primary.contrastText,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark
              }
            }}
          >
            Upload Data
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".bin"
            style={{ display: 'none' }}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <ToggleButtonGroup
              value={showGrid ? 'gridOn' : 'gridOff'}
              exclusive
              onChange={() => setShowGrid(!showGrid)}
              size="small"
            >
              <ToggleButton value="gridOn" sx={{ color: theme.palette.text.color }}>
                <GridOn fontSize="small" sx={{ mr: 1 }} /> Grid
              </ToggleButton>
              <ToggleButton value="gridOff" sx={{ color: theme.palette.text.color }}>
                <GridOff fontSize="small" sx={{ mr: 1 }} /> No Grid
              </ToggleButton>
            </ToggleButtonGroup>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel sx={{ color: theme.palette.text.color }}>Graphs per row</InputLabel>
              <Select
                value={graphsPerRow}
                onChange={(e) => setGraphsPerRow(e.target.value)}
                label="Graphs per row"
                sx={{
                  color: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.border.color,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.border.color,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.border.color,
                  }
                }}
              >
                <MenuItem value={1}>1</MenuItem>
                <MenuItem value={2}>2</MenuItem>
                <MenuItem value={3}>3</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {selectedDataset && (
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              {format(parseISO(selectedDataset.data[0].time), 'HH:mm:ss')} - {format(parseISO(selectedDataset.data[selectedDataset.data.length - 1].time), 'HH:mm:ss')}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Current Point Info */}
      {currentDataPoint && (
        <Box sx={{ 
          mb: 3,
          p: 2,
          backgroundColor: theme.palette.secondary.main,
          borderRadius: 2,
          boxShadow: theme.shadows[1],
          border: `1px solid ${theme.palette.border.color}`
        }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: theme.palette.text.color }}>
            Current Data Point: {formatTime(currentDataPoint.time)} (Point {currentTimeIndex + 1} of {selectedDataset?.data.length || 0})
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {Object.keys(selectedCharts).map(key => (
              selectedCharts[key] && (
                <Box key={key} sx={{ minWidth: 150 }}>
                  <Typography variant="subtitle2" sx={{ color: CHART_COLORS[key] }}>
                    {CHART_DISPLAY_NAMES[key]}
                  </Typography>
                  <Typography variant="h6" sx={{ color: theme.palette.text.color }}>
                    {Number(currentDataPoint[key] ?? 0).toFixed(2)}
                  </Typography>
                </Box>
              )
            ))}
          </Box>
        </Box>
      )}

      {/* Chart Selection */}
      <Box sx={{ 
        mb: 3,
        p: 2,
        backgroundColor: theme.palette.secondary.main,
        borderRadius: 2,
        boxShadow: theme.shadows[1],
        border: `1px solid ${theme.palette.border.color}`
      }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: theme.palette.text.color }}>
          Select Metrics to Display
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {Object.keys(selectedCharts).map(key => (
            <Chip
              key={key}
              label={CHART_DISPLAY_NAMES[key]}
              onClick={() => setSelectedCharts(prev => ({ ...prev, [key]: !prev[key] }))}
              color={selectedCharts[key] ? 'primary' : 'default'}
              variant={selectedCharts[key] ? 'filled' : 'outlined'}
              icon={
                !selectedCharts[key] ? (
                  <span style={{ fontSize: '12px', marginLeft: '6px' }}>❌</span>
                ) : null
              }
              sx={{ 
                borderColor: selectedCharts[key] ? CHART_COLORS[key] : theme.palette.border.color,
                backgroundColor: selectedCharts[key] ? 
                  (theme.palette.mode === 'dark' ? 
                    `${CHART_COLORS[key]}30` : 
                    `${CHART_COLORS[key]}10`) : 
                  'transparent',
                color: selectedCharts[key] ? CHART_COLORS[key] : theme.palette.text.color
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Charts */}
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: `repeat(${graphsPerRow}, 1fr)`,
        gap: 3,
        flex: 1
      }}>
        {Object.keys(selectedCharts).map(key => (
          selectedCharts[key] && renderMetricChart(key)
        ))}
      </Box>

      {/* Floating Play/Pause Button */}
      {showFloatingButton && (
        <Fab
          color="primary"
          aria-label="play/pause"
          onClick={togglePlayback}
          disabled={!selectedDataset}
          sx={{
            position: 'fixed',
            bottom: 32,
            right: 32,
            backgroundColor: theme.palette.primary.dark,
            color: theme.palette.primary.contrastText,
            '&:hover': {
              backgroundColor: theme.palette.primary.dark
            },
            '&:disabled': {
              backgroundColor: theme.palette.action.disabledBackground
            }
          }}
        >
          {isPlaying ? <Pause /> : <PlayArrow />}
        </Fab>
      )}

      {/* File Upload Modal */}
      <Modal
        open={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
          setUploadProgress(0);
        }}
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: '70%', md: '50%' },
          maxWidth: 500,
          bgcolor: theme.palette.secondary.main,
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          outline: 'none',
          border: `1px solid ${theme.palette.border.color}`
        }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: theme.palette.text.color }}>
            {uploadProgress < 100 ? 'Processing Data File' : 'File Processed Successfully'}
          </Typography>
          
          <Box sx={{ mt: 2, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <CircularProgress 
                variant={uploadProgress === 100 ? 'determinate' : 'indeterminate'}
                value={uploadProgress}
                size={80}
                thickness={4}
                color={uploadProgress === 100 ? 'success' : 'primary'}
              />
            </Box>
            <Typography variant="body1" sx={{ textAlign: 'center', color: theme.palette.text.color }}>
              {uploadProgress < 100 ? `Processing... ${uploadProgress}%` : 'Ready to visualize'}
            </Typography>
          </Box>

          {uploadProgress === 100 && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadProgress(0);
                }}
                sx={{ 
                  mr: 2,
                  color: theme.palette.text.color,
                  borderColor: theme.palette.border.color,
                  '&:hover': {
                    borderColor: theme.palette.border.color
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleUseUploadedData}
                sx={{
                  backgroundColor: theme.palette.primary.dark,
                  color: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark
                  }
                }}
              >
                Visualize Data
              </Button>
            </Box>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default HistoryVisualization;
